import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import { Activity, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

type Row = { id: string; action: string; entity_type: string | null; entity_id: string | null; actor_id: string | null; created_at: string };

export const Route = createFileRoute("/admin/audit")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Audit log — MAKAI" }] }),
  component: AuditPage,
});

function AuditPage() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!active) return;
    const { data } = await supabase.from("audit_logs").select("id,action,entity_type,entity_id,actor_id,created_at").eq("school_id", active.school_id).order("created_at", { ascending: false }).limit(200);
    setRows((data ?? []) as Row[]);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => !q || `${r.action} ${r.entity_type ?? ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AdminShell title="Audit log" subtitle="Sensitive actions taken in this school. Admin-only.">
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by action or entity" className="pl-9" />
      </div>
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No log entries match. Sensitive actions logged from the app will appear here.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card shadow-soft">
          {filtered.map((r) => (
            <li key={r.id} className="flex items-start gap-3 p-4">
              <Activity className="mt-1 h-4 w-4 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{r.action}</div>
                <div className="text-xs text-muted-foreground">{r.entity_type ?? "—"}{r.entity_id ? ` · ${r.entity_id.slice(0,8)}…` : ""}</div>
              </div>
              <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </AdminShell>
  );
}
